import { useState } from 'react'
import { format } from 'date-fns'
import { tokens } from '@/locales/translations/tokens'
import { ptBR, enUS } from 'date-fns/locale'
import {
  Loader2,
  Bell,
  Mail,
  MailOpen,
  BellOff,
  ExternalLink,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { usePlatformNotifications } from './hooks/use-platform-notifications'

export function PlatformNotificationsDropdown() {
  const [isOpen, setIsOpen] = useState(false)
  const { i18n, t } = useTranslation()
  const { notifications, loading, unreadCount, markAsViewed } =
    usePlatformNotifications(i18n.language)

  const locale = i18n.language === 'pt' ? ptBR : enUS

  const handleMarkAsViewed = () => {
    const unreadNotificationIds = notifications
      .filter((notification) => notification.viewed_at === null)
      .map((notification) => notification.external_id)

    if (unreadNotificationIds.length > 0) {
      markAsViewed(unreadNotificationIds)
    }
  }

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)

    if (!open && notifications.length > 0) {
      handleMarkAsViewed()
    }
  }

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM dd, HH:mm', {
      locale,
    })
  }

  const handleNotificationClick = (actionLink: string | null) => {
    if (actionLink) {
      window.open(actionLink, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant='ghost'
          size='sm'
          className='relative h-8 w-8 rounded-full'
        >
          <Bell className='h-4 w-4' />
          {unreadCount > 0 && (
            <span className='absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-semibold text-white'>
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className='w-[380px] p-0'
        align='end'
        side='bottom'
        sideOffset={8}
      >
        <DropdownMenuLabel className='flex items-center justify-between gap-2 px-4 py-3'>
          <span className='text-sm font-semibold'>
            {t(tokens.notifications.platform.title)}
          </span>
          <div className='flex items-center gap-2'>
            {notifications.length > 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={handleMarkAsViewed}
                      variant='outline'
                      size='sm'
                    >
                      {unreadCount > 0 ? (
                        <Mail className='h-4 w-4' />
                      ) : (
                        <MailOpen className='h-4 w-4' />
                      )}
                    </Button>
                  </TooltipTrigger>
                  {unreadCount > 0 && (
                    <TooltipContent>
                      <p>{t(tokens.notifications.common.markAllAsRead)}</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        {loading ? (
          <div className='flex items-center justify-center py-8'>
            <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
            <span className='ml-2 text-sm text-muted-foreground'>
              {t(tokens.notifications.common.loading)}
            </span>
          </div>
        ) : notifications.length === 0 ? (
          <div className='flex h-fit max-h-[400px] flex-col items-center justify-center px-6 py-12'>
            <div className='mb-4 rounded-full bg-muted/50 p-3'>
              <BellOff className='h-8 w-8 text-muted-foreground/60' />
            </div>
            <h3 className='mb-2 text-sm font-semibold text-foreground'>
              {t(tokens.notifications.platform.empty.title)}
            </h3>
            <p className='max-w-[280px] text-center text-xs leading-5 text-muted-foreground'>
              {t(tokens.notifications.platform.empty.description)}
            </p>
          </div>
        ) : (
          <ScrollArea className='h-fit max-h-[400px] overflow-y-auto'>
            <div className='space-y-0'>
              {notifications.map(
                (notification: Notification, index: number) => (
                  <div key={notification.external_id}>
                    <div
                      className={`group transition-all duration-200 hover:bg-accent/50 ${
                        notification.action_link
                          ? 'cursor-pointer hover:bg-accent/70'
                          : 'cursor-default'
                      }`}
                      onClick={() =>
                        handleNotificationClick(notification.action_link)
                      }
                    >
                      <div className='w-full space-y-2.5 p-4'>
                        <div className='flex items-start justify-between gap-3'>
                          <div className='min-w-0 flex-1'>
                            <h3 className='line-clamp-3 text-sm font-semibold leading-snug text-foreground'>
                              {notification.title}
                            </h3>
                          </div>
                          <div className='flex items-center gap-2'>
                            {notification.action_link && (
                              <ExternalLink className='h-3 w-3 text-muted-foreground/0 transition-colors group-hover:text-muted-foreground/60' />
                            )}
                            {notification.viewed_at === null && (
                              <div className='h-2 w-2 flex-shrink-0 rounded-full bg-blue-500' />
                            )}
                          </div>
                        </div>

                        {notification.description && (
                          <p className='line-clamp-5 text-xs leading-relaxed text-muted-foreground'>
                            {notification.description}
                          </p>
                        )}

                        <div className='pt-1'>
                          <time className='text-xs font-medium text-muted-foreground/80'>
                            {formatDate(notification.created_at)}
                          </time>
                        </div>
                      </div>
                    </div>
                    {index < notifications.length - 1 && (
                      <DropdownMenuSeparator className='my-0' />
                    )}
                  </div>
                )
              )}
            </div>
          </ScrollArea>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
